import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../../contexts/NotificationContext';
import { api } from '../../../services/api';

interface GoogleReview {
  author_name: string;
  rating: number;
  text: string;
  [key: string]: any;
}

interface GoogleBusinessSettings {
  place_id: string;
  api_key: string;
  business_name: string;
  enabled: boolean;
  reviews_count: number;
  min_rating: number;
  auto_refresh: boolean;
  refresh_interval_hours: number;
  cached_reviews: GoogleReview[];
  last_fetched: string | null;
}

interface GoogleBusinessSettingsProps {
  // This component manages its own settings state since it's not part of the main site settings
}

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #666;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-weight: 500;
  color: #333;
`;

const Button = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-right: 1rem;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled(Button)`
  background: #6c757d;
`;

const StatusIndicator = styled.div<{ $status: 'success' | 'error' | 'warning' | 'info' }>`
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  background-color: ${({ $status }) => {
    switch ($status) {
      case 'success': return '#d4edda';
      case 'error': return '#f8d7da';
      case 'warning': return '#fff3cd';
      case 'info': return '#d1ecf1';
      default: return '#f8f9fa';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'success': return '#155724';
      case 'error': return '#721c24';
      case 'warning': return '#856404';
      case 'info': return '#0c5460';
      default: return '#333';
    }
  }};
  border: 1px solid ${({ $status }) => {
    switch ($status) {
      case 'success': return '#c3e6cb';
      case 'error': return '#f5c6cb';
      case 'warning': return '#ffeaa7';
      case 'info': return '#bee5eb';
      default: return '#dee2e6';
    }
  }};
`;

const ReviewsPreview = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const ReviewItem = styled.div`
  background: white;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const ReviewAuthor = styled.div`
  font-weight: 500;
  color: #333;
`;

const ReviewRating = styled.div`
  color: #ffc107;
  font-size: 1.1rem;
`;

const ReviewText = styled.div`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GoogleBusinessSettings: React.FC<GoogleBusinessSettingsProps> = () => {
  const [settings, setSettings] = useState<GoogleBusinessSettings>({
    place_id: '',
    api_key: '',
    business_name: '',
    enabled: false,
    reviews_count: 5,
    min_rating: 4,
    auto_refresh: true,
    refresh_interval_hours: 24,
    cached_reviews: [],
    last_fetched: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [previewReviews, setPreviewReviews] = useState<GoogleReview[]>([]);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/google-business/settings');
      
      setSettings(response.data);
      
      // Verificar se cached_reviews é um array válido
      const cachedReviews = response.data.cached_reviews;
      if (Array.isArray(cachedReviews)) {
        // Filtrar apenas reviews válidos
        const validReviews = cachedReviews.filter((review: any) => 
          review && 
          typeof review === 'object' && 
          review.author_name && 
          typeof review.rating === 'number'
        );
        setPreviewReviews(validReviews.slice(0, 3));
      } else {
        setPreviewReviews([]);
      }
    } catch (error) {
      console.error('Error loading Google Business settings:', error);
      showNotification('Erro ao carregar configurações do Google Business', 'error');
      setPreviewReviews([]);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      await api.put('/google-business/settings', settings);

      showNotification('Configurações do Google Business salvas com sucesso!', 'success');
      loadSettings(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving Google Business settings:', error);
      showNotification('Erro ao salvar configurações do Google Business', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settings.place_id || !settings.api_key) {
      setTestStatus('error');
      showNotification('Preencha o Place ID e a API Key antes de testar', 'error');
      return;
    }

    try {
      setIsTesting(true);
      setTestStatus('info');
      
      const response = await api.post('/google-business/refresh-reviews');

      setTestStatus('success');
      
      // Verificar se reviews é um array válido
      const reviews = response.data.reviews;
      if (Array.isArray(reviews)) {
        const validReviews = reviews.filter((review: any) => 
          review && 
          typeof review === 'object' && 
          review.author_name && 
          typeof review.rating === 'number'
        );
        setPreviewReviews(validReviews.slice(0, 3));
        showNotification(`Conexão testada com sucesso! ${validReviews.length} avaliações encontradas`, 'success');
      } else {
        setPreviewReviews([]);
        showNotification('Conexão testada com sucesso!', 'success');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestStatus('error');
      showNotification('Erro ao testar conexão. Verifique suas credenciais', 'error');
    } finally {
      setIsTesting(false);
    }
  };

  const handleRefreshReviews = async () => {
    try {
      setIsLoading(true);
      
      const response = await api.post('/google-business/refresh-reviews');

      const data = response.data;
      
      // Verificar se reviews é um array válido
      if (Array.isArray(data.reviews)) {
        const validReviews = data.reviews.filter((review: any) => 
          review && 
          typeof review === 'object' && 
          review.author_name && 
          typeof review.rating === 'number'
        );
        setPreviewReviews(validReviews.slice(0, 3));
        showNotification(`Avaliações atualizadas com sucesso! ${validReviews.length} avaliações encontradas`, 'success');
      } else {
        setPreviewReviews([]);
        showNotification('Avaliações atualizadas com sucesso!', 'success');
      }
      loadSettings(); // Reload settings to update last_fetched
    } catch (error) {
      console.error('Error refreshing reviews:', error);
      showNotification('Erro ao atualizar avaliações', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const formatLastFetched = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes} minutos atrás`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} horas atrás`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} dias atrás`;
  };

  return (
    <>
      <Section>
        <SectionTitle>🌟 Google Business / Google Meu Negócio</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Configure a integração com o Google Meu Negócio para exibir avaliações dos clientes no seu site.
        </p>

        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            id="enabled"
            checked={settings.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
          />
          <CheckboxLabel htmlFor="enabled">
            Habilitar exibição de avaliações do Google
          </CheckboxLabel>
        </CheckboxContainer>

        {testStatus && (
          <StatusIndicator $status={testStatus as any}>
            {testStatus === 'success' && '✅ Conexão com Google Places API estabelecida com sucesso!'}
            {testStatus === 'error' && '❌ Erro na conexão. Verifique suas credenciais.'}
            {testStatus === 'warning' && '⚠️ Atenção: Configuração incompleta.'}
            {testStatus === 'info' && '🔄 Testando conexão...'}
          </StatusIndicator>
        )}

        <FormGroup>
          <Label htmlFor="business_name">Nome do Negócio</Label>
          <Input
            id="business_name"
            type="text"
            value={settings.business_name}
            onChange={(e) => handleChange('business_name', e.target.value)}
            placeholder="Nome da sua empresa no Google"
            disabled={!settings.enabled}
          />
          <HelpText>Nome que aparece no Google Meu Negócio (opcional, apenas para referência)</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="place_id">Google Place ID *</Label>
          <Input
            id="place_id"
            type="text"
            value={settings.place_id}
            onChange={(e) => handleChange('place_id', e.target.value)}
            placeholder="ChIJXxxxxxxxxxxxxxxxxxxx"
            disabled={!settings.enabled}
          />
          <HelpText>
            Encontre seu Place ID em: <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Google Place ID Finder</a>
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="api_key">Google Places API Key *</Label>
          <Input
            id="api_key"
            type="password"
            value={settings.api_key}
            onChange={(e) => handleChange('api_key', e.target.value)}
            placeholder="AIzaSyXxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            disabled={!settings.enabled}
          />
          <HelpText>
            Obtenha sua API Key no <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Google Cloud Console</a>. 
            Habilite a "Places API" para seu projeto.
          </HelpText>
        </FormGroup>

        <div style={{ marginBottom: '2rem' }}>
          <Button 
            onClick={handleTestConnection} 
            disabled={!settings.enabled || isTesting || !settings.place_id || !settings.api_key}
          >
            {isTesting ? '🔄 Testando...' : '🔍 Testar Conexão'}
          </Button>
          <SecondaryButton 
            onClick={handleRefreshReviews}
            disabled={!settings.enabled || isLoading || !settings.place_id || !settings.api_key}
          >
            {isLoading ? '🔄 Atualizando...' : '🔄 Atualizar Avaliações'}
          </SecondaryButton>
        </div>
      </Section>

      {settings.enabled && (
        <Section>
          <SectionTitle>⚙️ Configurações de Exibição</SectionTitle>
          
          <FormGrid>
            <FormGroup>
              <Label htmlFor="reviews_count">Quantidade de Avaliações</Label>
              <Select
                id="reviews_count"
                value={settings.reviews_count}
                onChange={(e) => handleChange('reviews_count', parseInt(e.target.value))}
              >
                <option value={3}>3 avaliações</option>
                <option value={5}>5 avaliações</option>
                <option value={10}>10 avaliações</option>
                <option value={20}>20 avaliações</option>
              </Select>
              <HelpText>Número máximo de avaliações a exibir no site</HelpText>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="min_rating">Avaliação Mínima</Label>
              <Select
                id="min_rating"
                value={settings.min_rating}
                onChange={(e) => handleChange('min_rating', parseInt(e.target.value))}
              >
                <option value={1}>1 estrela ou mais</option>
                <option value={2}>2 estrelas ou mais</option>
                <option value={3}>3 estrelas ou mais</option>
                <option value={4}>4 estrelas ou mais</option>
                <option value={5}>Apenas 5 estrelas</option>
              </Select>
              <HelpText>Filtrar avaliações com nota mínima</HelpText>
            </FormGroup>
          </FormGrid>

          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              id="auto_refresh"
              checked={settings.auto_refresh}
              onChange={(e) => handleChange('auto_refresh', e.target.checked)}
            />
            <CheckboxLabel htmlFor="auto_refresh">
              Atualização automática de avaliações
            </CheckboxLabel>
          </CheckboxContainer>

          {settings.auto_refresh && (
            <FormGroup>
              <Label htmlFor="refresh_interval_hours">Intervalo de Atualização</Label>
              <Select
                id="refresh_interval_hours"
                value={settings.refresh_interval_hours}
                onChange={(e) => handleChange('refresh_interval_hours', parseInt(e.target.value))}
              >
                <option value={1}>A cada 1 hora</option>
                <option value={6}>A cada 6 horas</option>
                <option value={12}>A cada 12 horas</option>
                <option value={24}>A cada 24 horas</option>
                <option value={168}>A cada 7 dias</option>
              </Select>
              <HelpText>Com que frequência buscar novas avaliações automaticamente</HelpText>
            </FormGroup>
          )}

          <p style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            🕐 Última atualização: {formatLastFetched(settings.last_fetched)}
          </p>
        </Section>
      )}

      {settings.enabled && Array.isArray(previewReviews) && previewReviews.length > 0 && (
        <Section>
          <SectionTitle>👀 Prévia das Avaliações</SectionTitle>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Estas são as avaliações que aparecerão no seu site (mostrando apenas as 3 primeiras):
          </p>
          
          <ReviewsPreview>
            {previewReviews.map((review, index) => {
              // Verificações de segurança para evitar erros
              if (!review || typeof review !== 'object') {
                return null;
              }

              const authorName = review.author_name || 'Usuário Anônimo';
              const rating = typeof review.rating === 'number' ? review.rating : 5;
              const reviewText = review.text || '';
              
              return (
                <ReviewItem key={index}>
                  <ReviewHeader>
                    <ReviewAuthor>{authorName}</ReviewAuthor>
                    <ReviewRating>{renderStars(rating)}</ReviewRating>
                  </ReviewHeader>
                  <ReviewText>
                    {reviewText.length > 150 
                      ? reviewText.substring(0, 150) + '...' 
                      : reviewText}
                  </ReviewText>
                </ReviewItem>
              );
            })}
          </ReviewsPreview>
        </Section>
      )}

      <Section>
        <Button onClick={handleSave} disabled={isLoading}>
          {isLoading ? '💾 Salvando...' : '💾 Salvar Configurações'}
        </Button>
      </Section>
    </>
  );
};

export default GoogleBusinessSettings;
